/**
 * Demo Account Seeder
 *
 * Creates pre-populated demo accounts with realistic progress data.
 * Used for testing and demonstration purposes.
 *
 * Demo ecosystem:
 * - 5 learners across 2 schools
 * - 2 parents (each linked to their children)
 * - 2 teachers (one per school/classroom)
 * - 1 admin (global viewpoint)
 */

import { AppState, ModuleId, QuestionState, FamilyMasteryState } from '../types'
import { MultiAccountStateV3 } from './accountMigration'
import {
  generateAccountId,
  createInitialAccountsState,
  linkParentToLearner,
  linkTeacherToLearner,
} from './accountHelpers'
import {
  LearnerAccount,
  ParentAccount,
  TeacherAccount,
  AdminAccount,
} from '../types/accounts'
import { debug } from './logger'

/**
 * Create a demo learner account with realistic progress
 */
function createDemoLearner(
  name: string,
  level: number,
  xp: number,
  progressData: {
    completedQuestions: Record<ModuleId, number>
    masteryScores: Record<string, number>
  },
): {
  account: LearnerAccount
  data: AppState
} {
  const now = Date.now()
  const account: LearnerAccount = {
    id: generateAccountId(),
    role: 'learner',
    displayName: name,
    createdAt: now - 30 * 24 * 60 * 60 * 1000,
    lastActive: now - Math.floor(Math.random() * 3 * 24 * 60 * 60 * 1000),
  }

  // Generate question states
  const questionStates: Record<string, QuestionState> = {}
  Object.entries(progressData.completedQuestions).forEach(
    ([moduleId, count]) => {
      for (let i = 0; i < count; i++) {
        const questionId = `${moduleId}_demo_q${i}`
        const stateKey = `${questionId}_g6` // Group 6 scoped
        questionStates[stateKey] = {
          questionId,
          status: 'practiced',
          attempts: Math.floor(Math.random() * 3) + 1,
          correctAttempts: Math.floor(Math.random() * 2) + 1,
          lastAttemptDate:
            now - Math.floor(Math.random() * 7 * 24 * 60 * 60 * 1000),
          masteryPoints: Math.floor(Math.random() * 50) + 50,
          history: [
            {
              isCorrect: true,
              timestamp: now - 3 * 24 * 60 * 60 * 1000,
            },
            {
              isCorrect: Math.random() > 0.3,
              timestamp: now - 1 * 24 * 60 * 60 * 1000,
            },
          ],
        }
      }
    },
  )

  // Generate family mastery records
  const familyMastery: Record<string, FamilyMasteryState> = {}
  Object.entries(progressData.masteryScores).forEach(([familyId, score]) => {
    const masteryKey = `${familyId}_g6` // Group 6 scoped
    familyMastery[masteryKey] = {
      familyId,
      masteryScore: score,
      attempts: Math.floor(Math.random() * 20) + 10,
      correct: Math.floor((score / 100) * 30),
      lastSeen: now - Math.floor(Math.random() * 7 * 24 * 60 * 60 * 1000),
      confidence: Math.min(1, score / 100 + 0.2),
      history: [
        {
          questionId: `${familyId}_demo_1`,
          isCorrect: true,
          timestamp: now - 5 * 24 * 60 * 60 * 1000,
          tier: 3,
        },
        {
          questionId: `${familyId}_demo_2`,
          isCorrect: score > 70,
          timestamp: now - 2 * 24 * 60 * 60 * 1000,
          tier: 4,
        },
      ],
    }
  })
  const data: AppState = {
    stats: {
      name,
      xp,
      level,
    },
    progress: {
      Metriek: {
        cheatSheetViewed: xp > 500,
      },
      Getalbegrip: {
        cheatSheetViewed: xp > 300,
      },
      TijdGeld: {
        cheatSheetViewed: xp > 400,
      },
      SpellingWW: {
        cheatSheetViewed: xp > 800,
      },
      SpellingRegel: {
        cheatSheetViewed: xp > 800,
      },
      Taalbegrip: {
        cheatSheetViewed: xp > 1000,
      },
      Studievaardig: {
        cheatSheetViewed: false,
      },
      Topo: {
        cheatSheetViewed: false,
      },
    },
    questionStates,
    familyMastery,
    placementResults: {},
    pendingLevelUp: undefined,
  }
  return {
    account,
    data,
  }
}

/**
 * Create demo parent account
 */
function createDemoParent(name: string, email?: string): ParentAccount {
  const now = Date.now()
  return {
    id: generateAccountId(),
    role: 'parent',
    displayName: name,
    email,
    createdAt: now - 30 * 24 * 60 * 60 * 1000,
    lastActive: now - Math.floor(Math.random() * 2 * 24 * 60 * 60 * 1000),
  }
}

/**
 * Create demo teacher account
 */
function createDemoTeacher(
  name: string,
  schoolName: string,
  classroomName: string,
  email?: string,
): TeacherAccount {
  const now = Date.now()
  return {
    id: generateAccountId(),
    role: 'teacher',
    displayName: name,
    email,
    createdAt: now - 60 * 24 * 60 * 60 * 1000,
    lastActive: now - Math.floor(Math.random() * 1 * 24 * 60 * 60 * 1000),
    schoolName,
    classroomName,
  }
}

/**
 * Create demo admin account
 */
function createDemoAdmin(name: string, email?: string): AdminAccount {
  const now = Date.now()
  return {
    id: generateAccountId(),
    role: 'admin',
    displayName: name,
    email,
    createdAt: now - 90 * 24 * 60 * 60 * 1000,
    lastActive: now,
  }
}

/**
 * Generate complete demo account state with realistic data.
 *
 * Creates a realistic school ecosystem:
 * - Basisschool De Regenboog (Groep 6A): Meester Jay → Djayden, Emma, Noah
 * - Basisschool Het Kompas (Groep 6B): Juf Lisa → Sophie, Liam
 * - Mama (parent of Djayden & Emma)
 * - Papa van Noah (parent of Noah)
 * - Admin (global viewpoint)
 */
export function generateDemoAccounts(): MultiAccountStateV3 {
  // ── School 1: Basisschool De Regenboog ──────────────────────────────────

  const djayden = createDemoLearner('Djayden', 5, 1200, {
    completedQuestions: {
      TijdGeld: 55,
      Getalbegrip: 35,
      Metriek: 20,
      SpellingWW: 10,
      SpellingRegel: 5,
      Taalbegrip: 0,
      Studievaardig: 0,
      Topo: 0,
    },
    masteryScores: {
      'TGL-TIJD-ANALOOG': 85,
      'TGL-TIJD-DIGITAAL': 90,
      'TGL-GELD-MAAK': 75,
      'TGL-GELD-WISSEL': 80,
      'GET-BREUKEN-BASIS': 70,
      'GET-OPTELLEN-AFTREKKEN': 85,
      'MET-LENGTE-METEN': 65,
    },
  })

  const emma = createDemoLearner('Emma', 3, 600, {
    completedQuestions: {
      Metriek: 40,
      SpellingWW: 25,
      Getalbegrip: 15,
      TijdGeld: 8,
      SpellingRegel: 0,
      Taalbegrip: 0,
      Studievaardig: 0,
      Topo: 0,
    },
    masteryScores: {
      'MET-LENGTE-METEN': 70,
      'MET-GEWICHT-METEN': 65,
      'MET-INHOUD-METEN': 60,
      'SWW-WERKWOORDEN-D-T': 55,
      'SWW-WERKWOORDEN-DT': 50,
      'GET-TAFELS-BASIS': 60,
    },
  })

  const noah = createDemoLearner('Noah', 4, 950, {
    completedQuestions: {
      Getalbegrip: 45,
      TijdGeld: 30,
      Metriek: 25,
      SpellingWW: 15,
      SpellingRegel: 10,
      Taalbegrip: 5,
      Studievaardig: 0,
      Topo: 0,
    },
    masteryScores: {
      'GET-BREUKEN-BASIS': 80,
      'GET-OPTELLEN-AFTREKKEN': 75,
      'GET-TAFELS-BASIS': 90,
      'TGL-TIJD-ANALOOG': 70,
      'TGL-GELD-MAAK': 65,
      'MET-LENGTE-METEN': 60,
      'SWW-WERKWOORDEN-D-T': 45,
    },
  })

  // ── School 2: Basisschool Het Kompas ────────────────────────────────────

  const sophie = createDemoLearner('Sophie', 6, 1500, {
    completedQuestions: {
      SpellingWW: 50,
      SpellingRegel: 40,
      Taalbegrip: 35,
      Getalbegrip: 30,
      TijdGeld: 25,
      Metriek: 20,
      Studievaardig: 10,
      Topo: 5,
    },
    masteryScores: {
      'SWW-WERKWOORDEN-D-T': 92,
      'SWW-WERKWOORDEN-DT': 88,
      'SPR-KLANK-BASIS': 85,
      'TAA-LEZEN-KERN': 80,
      'GET-BREUKEN-BASIS': 75,
      'TGL-TIJD-ANALOOG': 70,
      'MET-LENGTE-METEN': 65,
      'STU-BRONNEN': 55,
    },
  })

  const liam = createDemoLearner('Liam', 2, 350, {
    completedQuestions: {
      TijdGeld: 20,
      Metriek: 15,
      Getalbegrip: 10,
      SpellingWW: 5,
      SpellingRegel: 0,
      Taalbegrip: 0,
      Studievaardig: 0,
      Topo: 0,
    },
    masteryScores: {
      'TGL-TIJD-ANALOOG': 45,
      'TGL-GELD-MAAK': 40,
      'MET-LENGTE-METEN': 35,
      'GET-TAFELS-BASIS': 30,
    },
  })

  // ── Parents ─────────────────────────────────────────────────────────────

  const mama = createDemoParent('Mama', 'mama@voorbeeld.nl')
  const papaNoah = createDemoParent('Papa van Noah', 'papa.noah@voorbeeld.nl')

  // ── Teachers ────────────────────────────────────────────────────────────

  const meesterJay = createDemoTeacher(
    'Meester Jay',
    'Basisschool De Regenboog',
    'Groep 6A',
    'jay@regenboog.nl',
  )

  const jufLisa = createDemoTeacher(
    'Juf Lisa',
    'Basisschool Het Kompas',
    'Groep 6B',
    'lisa@hetkompas.nl',
  )

  // ── Admin ───────────────────────────────────────────────────────────────

  const admin = createDemoAdmin('Admin', 'admin@meesterdjay.nl')

  // ── Build accounts state ────────────────────────────────────────────────

  let accountsState = createInitialAccountsState()

  // Replace with our demo accounts
  accountsState.accountsById = {
    [djayden.account.id]: djayden.account,
    [emma.account.id]: emma.account,
    [noah.account.id]: noah.account,
    [sophie.account.id]: sophie.account,
    [liam.account.id]: liam.account,
    [mama.id]: mama,
    [papaNoah.id]: papaNoah,
    [meesterJay.id]: meesterJay,
    [jufLisa.id]: jufLisa,
    [admin.id]: admin,
  }

  // Set Djayden as active account
  accountsState.activeAccountId = djayden.account.id

  // ── Link relationships ──────────────────────────────────────────────────

  // Mama → Djayden & Emma
  accountsState = linkParentToLearner(
    accountsState,
    mama.id,
    djayden.account.id,
  )
  accountsState = linkParentToLearner(accountsState, mama.id, emma.account.id)

  // Papa van Noah → Noah
  accountsState = linkParentToLearner(
    accountsState,
    papaNoah.id,
    noah.account.id,
  )

  // Meester Jay (De Regenboog) → Djayden, Emma, Noah
  accountsState = linkTeacherToLearner(
    accountsState,
    meesterJay.id,
    djayden.account.id,
  )
  accountsState = linkTeacherToLearner(
    accountsState,
    meesterJay.id,
    emma.account.id,
  )
  accountsState = linkTeacherToLearner(
    accountsState,
    meesterJay.id,
    noah.account.id,
  )

  // Juf Lisa (Het Kompas) → Sophie, Liam
  accountsState = linkTeacherToLearner(
    accountsState,
    jufLisa.id,
    sophie.account.id,
  )
  accountsState = linkTeacherToLearner(
    accountsState,
    jufLisa.id,
    liam.account.id,
  )

  // ── Build learner data map ──────────────────────────────────────────────

  const learnerData: Record<string, AppState> = {
    [djayden.account.id]: djayden.data,
    [emma.account.id]: emma.data,
    [noah.account.id]: noah.data,
    [sophie.account.id]: sophie.data,
    [liam.account.id]: liam.data,
  }

  const demoState: MultiAccountStateV3 = {
    schemaVersion: 3,
    accounts: accountsState,
    learnerData,
  }

  debug('✨ Demo accounts created:')
  debug(`   📚 Basisschool De Regenboog (Groep 6A):`)
  debug(`      - Djayden (Leerling, Level ${djayden.data.stats.level})`)
  debug(`      - Emma (Leerling, Level ${emma.data.stats.level})`)
  debug(`      - Noah (Leerling, Level ${noah.data.stats.level})`)
  debug(`      - Meester Jay (Docent)`)
  debug(`   📚 Basisschool Het Kompas (Groep 6B):`)
  debug(`      - Sophie (Leerling, Level ${sophie.data.stats.level})`)
  debug(`      - Liam (Leerling, Level ${liam.data.stats.level})`)
  debug(`      - Juf Lisa (Docent)`)
  debug(`   👪 Mama (Ouder, gekoppeld aan Djayden & Emma)`)
  debug(`   👪 Papa van Noah (Ouder, gekoppeld aan Noah)`)
  debug(`   🛡️ Admin (Beheerder, globaal overzicht)`)

  return demoState
}
